/**
 * 业务接口
 *
 * 统一使用 libs/http-client（axios）：
 * - 公共请求头（Authorization、apikey、channel、lang）由请求库注入
 * - 401（code -201 / 401 + status 0）时通过 bridge 让 App 静默刷新 Token 并自动重试
 * - 本页按完整响应体 { code, msg, data } 判断核销/登记结果，且登记前会提前发起 dry-run 请求再稍后 await，
 *   因此所有接口函数统一返回响应体、永不 reject：网络/HTTP 异常返回 code 10000 + "網路錯誤"
 *   （与迁移前一致，也避免提前创建的 Promise 在被 await 之前产生未处理的 rejection）
 */
import {
  createHttpClient,
  HttpError,
  type ApiEnvelope,
} from "../../libs/http-client";
import { bridge } from "./bridge";

// ============ 环境配置 ============

type ProfilesType = {
  dev: string;
  test: string;
  prod: string;
};

export const chiefCommonh5: ProfilesType = {
  dev: "https://common-h5.test.stx365.com/",
  test: "https://common-h5.test.stx365.com/",
  prod: "https://common-h5.chiefgroup.com.hk/",
};

const mode = __MODE__ ?? "prod";
export const commonh5Url =
  chiefCommonh5[mode as keyof ProfilesType] ?? chiefCommonh5.prod;

const http = createHttpClient({
  baseURL: "/chiefApi/chief-operations",
  bridge,
});

/** 公开请求（站外）：不带 Authorization，不触发 401 刷新 */
const publicHttp = createHttpClient({
  baseURL: "/chiefApi/chief-operations",
  shouldAttachAuth: () => false,
});

const NETWORK_ERROR_CODE = 10000;

function isEnvelope(data: unknown): data is ApiEnvelope<never> {
  return typeof data === "object" && data !== null && "code" in data;
}

/** 请求失败时尽量还原后端响应体（页面据此读 code/errorCode），否则返回网络错误 */
async function toEnvelope<T>(
  request: Promise<ApiEnvelope<T>>,
): Promise<ApiEnvelope<T>> {
  try {
    return await request;
  } catch (error) {
    if (
      error instanceof HttpError &&
      (error.type === "http" || error.type === "unauthorized") &&
      isEnvelope(error.data)
    ) {
      return error.data as ApiEnvelope<T>;
    }
    console.error("request error:", error);
    return { code: NETWORK_ERROR_CODE, msg: "網路錯誤" };
  }
}

// ============ 帮助中心文章接口 ============

/** 周年活动规则页文章 slug（测试、生产环境一致） */
export const RULES_SLUG = "superbirthdayrewards";

export interface ArticleDetail {
  urlSlug: string;
  updateTime: string;
  id: number;
  seoDesc: string;
  seoKeywords: string;
  seoTitle: string;
  title: string;
  content: string;
}

export interface ArticleSlugResponse {
  path: Array<{
    sortOrder: number;
    name: string;
    id: number;
    status: number;
    parentId?: number;
  }>;
  relatedArticles: ArticleDetail[];
  article: ArticleDetail;
}

/** 通过 slug 获取帮助中心文章，可覆盖请求语言。 */
export function fetchHelpCenterArticle(
  slug: string = RULES_SLUG,
  langOverride?: string,
): Promise<ApiEnvelope<ArticleSlugResponse>> {
  return toEnvelope(
    http.get<ApiEnvelope<ArticleSlugResponse>>(
      `/help-center/article/slug/${encodeURIComponent(slug)}`,
      {
        silent: true,
        raw: true,
        ...(langOverride ? { headers: { lang: langOverride } } : {}),
      },
    ),
  );
}

// ============ 抽奖活动接口（与 lottery 项目同一套） ============

/** 奖品项 */
export interface AwardItem {
  /** 层级名称（根据语言返回） */
  levelName: string;
  /** 奖品描述（根据语言返回） */
  awardDesc: string;
  /** 顺序 */
  sequence: number;
  /** 中奖名额 */
  winnerCount: number;
}

/** 我的签池信息项 */
export interface UserTicketDetail {
  /** 签号 */
  ticketNumber?: string;
  /** 任务 */
  task?: string;
  /** 状态 0-已作废 1-有效 2-已中签 3-未中签 */
  status?: number;
  /** 获奖等级名（仅已中签时有值） */
  levelName?: string;
  /** 创建时间 */
  createTime?: string;
  /** traceId */
  traceId?: string;
}

/** 签池信息 */
export interface PoolInfo {
  /** 总签数 */
  totalTicketCount: number;
  /** 参与用户数 */
  participantCount: number;
  /** 中签人数 */
  winnerCount: number;
  /** 我的签数 */
  myTicketCount: number;
  /** 我的签池信息 */
  userTicketDetail: UserTicketDetail[];
}

/** 活动详情 */
export interface ActivityDetail {
  /** 活动编号 */
  activityCode: string;
  /** 活动名称（根据语言返回） */
  activityName: string;
  /** 活动副标题（根据语言返回） */
  activitySubtitle: string;
  /** 活动描述（根据语言返回） */
  activityDesc: string;
  /** 活动规则（根据语言返回） */
  activityRules: string;
  /** banner图url（根据语言返回） */
  bannerUrl: string;
  /** 奖品一览图url（根据语言返回） */
  awardImgUrl: string;
  /** 活动状态 0-未开始 1-进行中 2-待开奖 3-已完成 */
  status: number;
  /** 活动开始时间 */
  activityFrom: string;
  /** 活动结束时间 */
  activityTo: string;
  /** 抽奖日期 */
  lotteryDate: string;
  /** 奖品列表 */
  awards: AwardItem[];
  /** 签池信息 */
  poolInfo: PoolInfo;
}

/**
 * 查询活动详情【需登录，站内用】
 * GET /chief-operations/api/v1/lottery/activity/detail
 */
export function getActivityDetail(
  activityCode: string,
): Promise<ApiEnvelope<ActivityDetail>> {
  return toEnvelope(
    http.get<ApiEnvelope<ActivityDetail>>(
      "/api/v1/lottery/activity/detail",
      { silent: true, raw: true, params: { activityCode } },
    ),
  );
}

/**
 * 查询活动详情【不登录，站外用】
 * GET /chief-operations/api/v1/lottery/activity/info
 */
export function getActivityInfo(
  activityCode: string,
): Promise<ApiEnvelope<ActivityDetail>> {
  return toEnvelope(
    publicHttp.get<ApiEnvelope<ActivityDetail>>(
      "/api/v1/lottery/activity/info",
      { silent: true, raw: true, params: { activityCode } },
    ),
  );
}

// ============ 周年活动登记与二维码核销 ============

export interface ActivityEnrollmentStatus {
  /** 是否已登记活动（已参与任意一个批量任务） */
  registered?: boolean;
  /** 当前用户是否具备活动参与资格（不包含开户状态） */
  isEligible?: boolean;
  /** 用户是否已开户：KYC 审核通过且账户未关闭 */
  accountOpened?: boolean;
  traceId?: string;
}

/**
 * 查询当前用户的活动登记状态【需登录，站内用】
 * GET /chief-operations/api/v1/task-center/activities/{activityKey}/enrollment-status
 */
export function getEnrollmentStatus(
  activityKey: string,
): Promise<ApiEnvelope<ActivityEnrollmentStatus>> {
  return toEnvelope(
    http.get<ApiEnvelope<ActivityEnrollmentStatus>>(
      `/api/v1/task-center/activities/${encodeURIComponent(activityKey)}/enrollment-status`,
      { silent: true, raw: true },
    ),
  );
}

export type RedeemErrorCode =
  | "INVALID_CODE"
  | "ALREADY_USED"
  | "USER_ALREADY_REDEEMED"
  | "ACCOUNT_NOT_OPENED"
  | "NOT_ELIGIBLE"
  | "ACTIVITY_NOT_IN_PERIOD"
  | "LOCK_NOT_ACQUIRED"
  | "ORCHESTRATION_FAILED"
  | "SYSTEM_ERROR";

export interface QrcodeRedeemResult {
  success?: boolean;
  redeemed?: boolean;
  redeemTime?: string;
  errorCode?: RedeemErrorCode;
  ineligibleReason?: string;
  registered?: boolean;
  offlineTaskCompleted?: boolean;
  rewardIssued?: boolean;
  partialSuccess?: boolean;
  traceId?: string;
}

export type EnrollErrorCode =
  | "NOT_IN_PERIOD"
  | "ACCOUNT_NOT_OPENED"
  | "NOT_ELIGIBLE";

export interface ActivityEnrollResult {
  registered?: boolean;
  enrollments?: Record<string, unknown>[];
  errorCode?: EnrollErrorCode;
  ineligibleReason?: string;
  failedTaskId?: string;
  traceId?: string;
  /** TODO: dry-run 返回结构确认后补充资格校验及幂等字段。 */
  [key: string]: unknown;
}

export function redeemQrcode(
  qrcodeToken: string,
  activityKey: string,
): Promise<ApiEnvelope<QrcodeRedeemResult>> {
  return toEnvelope(
    http.post<ApiEnvelope<QrcodeRedeemResult>>(
      "/api/v1/qrcode-redeem/redeem",
      { qrcodeToken, activityKey },
      { silent: true, raw: true },
    ),
  );
}

export function enrollActivity(
  activityKey: string,
  dryRun: boolean,
): Promise<ApiEnvelope<ActivityEnrollResult>> {
  // 首次资格校验不传 requestId；待后端明确返回结构后再补幂等键。
  return toEnvelope(
    http.post<ApiEnvelope<ActivityEnrollResult>>(
      `/api/v1/task-center/activities/${encodeURIComponent(activityKey)}/enroll`,
      { dryRun },
      { silent: true, raw: true },
    ),
  );
}
